import { useCallback, useState } from 'react';
import { Field } from 'react-final-form';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';

import { IKnmAttachment } from 'APIPackage/KnmModule/interfaces/IKnmAttachment';
import { MB } from 'CorePackage/MainModule/constants/fileSize';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { knmDocumentsUpload } from 'KnmPackage/KnmDocumentsModule/actions/knmDocumentsActions';
import { useKnmDocumentAttachment } from 'KnmPackage/MainModule/hooks/useKnmDocumentAttachment';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { IKnmFormAttachment } from 'KnmPackage/MainModule/interfaces/IKnmFormAttachment';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { IKnmPmFormResultDecision } from 'KnmPmPackage/MainModule/interfaces/IKnmPmFormResultDecision';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Button } from 'UIPackage/MainModule/components/Button';
import { UploadFile } from 'UIPackage/MainModule/components/UploadFile';
import { UploadModal } from 'UIPackage/MainModule/components/UploadModal';

const documentValidExtensions = ['pdf', 'png', 'jpeg', 'ktd', 'rtf', 'rar', 'zip'];
const documentValidExtensionsString = `.${documentValidExtensions.join(',.')}`;
const maxFileSize = 10 * MB;

interface IPmResultOfInspectionResultDecisionsFileInputProps {
  formRef: IKnmFormRef | undefined;
  watchOnly: boolean;
  value: IKnmPmFormResultDecision;
  name: string;
}
export const PmResultOfInspectionResultDecisionsFileInput = ({
  formRef,
  name,
  value,
  watchOnly,
}: IPmResultOfInspectionResultDecisionsFileInputProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const { uploadKnmDocumentAttachment } = useKnmDocumentAttachment();
  const { downloadKnmDocumentAttachment } = useKnmDocumentAttachment();
  const downloadAttachment = (attachment: IKnmFormAttachment) => () => {
    const { fileName, fileSize } = attachment;
    const filePath = attachment.filePath || null;
    const mimeType = attachment.mimeType || null;

    downloadKnmDocumentAttachment({ fileName, filePath, fileSize, mimeType });
  };

  const [isModalVisible, setIsModalVisible] = useState(false);

  const handleSaveDocument = useCallback(
    async (signedDocument: File, signatureInfo?: IKnmAttachment['signatureInfo']) => {
      const uploadedAttachment = await uploadKnmDocumentAttachment(signedDocument);

      if (uploadedAttachment) {
        const newUploadedAttachment: IKnmFormAttachment = {
          ...uploadedAttachment,
          signatureInfo: signatureInfo || null,
          signed: !!signatureInfo,
          isNew: true,
        };

        formRef?.current.change(name as keyof IKnmForm, newUploadedAttachment);
      }
    },
    [formRef, name, uploadKnmDocumentAttachment],
  );

  const handleFileButtonClick = useCallback(() => {
    setIsModalVisible(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setIsModalVisible(false);
  }, []);

  const handleModalSave = useCallback(
    (document: File, sign: File | null) => {
      if (sign) {
        dispatch(knmDocumentsUpload(document, [sign], handleSaveDocument));
      } else {
        handleSaveDocument(document);
      }
    },
    [dispatch, handleSaveDocument],
  );

  const handleRemoveFile = useCallback(() => {
    formRef?.current.change(name as keyof IKnmForm, undefined);
  }, []);

  const file = value.attachment;

  return (
    <>
      <Field name={name}>
        {({ meta }) => {
          return (
            <FieldBlock
              label="Документ решения"
              id={name}
              button={
                <>
                  {!file && (
                    <Button
                      className="shared-btn-add"
                      text="Добавить"
                      type="button"
                      variant="link"
                      color="primary"
                      disabled={watchOnly}
                      onClick={handleFileButtonClick}
                    />
                  )}
                  {file && (
                    <UploadFile
                      fileName={file.fileName ?? ''}
                      fileSize={file.fileSize}
                      onRemove={watchOnly ? undefined : handleRemoveFile}
                      onFileNameClick={!file?.isNew && file?.guid ? downloadAttachment(file) : undefined}
                    />
                  )}
                  {(meta.modified || meta.submitFailed) && meta.error && (
                    <p className="shared-input-error">{meta.error}</p>
                  )}
                </>
              }
            />
          );
        }}
      </Field>

      <UploadModal
        accept={documentValidExtensionsString}
        modalTitle="Документ решения"
        onClose={handleModalClose}
        visible={isModalVisible}
        onSave={handleModalSave}
        maxFileSize={maxFileSize}
        uploadButtonTitle="Добавить"
      />
    </>
  );
};
